import { usePrepareOrganizationCheckoutMutation } from "@api/flatRateBilling.api";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeError } from "@stripe/stripe-js";
import { useScope } from "@state/scopeContext.lib";
import { useCallback, useState } from "react";
import { ENROLL_ACTION_QUERY_PARAM, EnrollAction } from "./lib";

interface UseEnrollmentFlowParams {
    action?: EnrollAction;
}

export function useEnrollmentFlow({ action }: UseEnrollmentFlowParams) {
    const [paymentBusy, setPaymentBusy] = useState(false);
    const [stripeError, setStripeError] = useState<StripeError | null>(null);
    const [checkoutError, setCheckoutError] = useState<string | null>(null);
    const stripe = useStripe();
    const elements = useElements();
    const { organizationId } = useScope();
    const prepareCheckoutSession = usePrepareOrganizationCheckoutMutation();

    const handleEnroll = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (!stripe || !elements || !organizationId) {
                return null;
            }

            try {
                setPaymentBusy(true);
                setStripeError(null);
                setCheckoutError(null);

                const submitResult = await elements.submit();

                if (submitResult.error) {
                    setStripeError(submitResult.error);
                    return null;
                }

                const { clientSecret, intentId } =
                    await prepareCheckoutSession.mutateAsync(organizationId);

                const afterUrl = new URL(window.location.href);

                if (action) {
                    afterUrl.searchParams.set(
                        ENROLL_ACTION_QUERY_PARAM,
                        action,
                    );
                }

                const after = `${afterUrl.pathname}${afterUrl.search}`;
                const returnUrl = new URL(`${window.location.origin}/enroll`);

                returnUrl.searchParams.set("after", after);
                returnUrl.searchParams.set("setupIntentId", intentId);

                const { error } = await stripe.confirmSetup({
                    elements,
                    clientSecret,
                    confirmParams: {
                        return_url: returnUrl.toString(),
                    },
                });

                if (error) {
                    setStripeError(error);
                }
            } catch {
                setCheckoutError(
                    "Something went wrong preparing checkout. Please try again.",
                );
            } finally {
                setPaymentBusy(false);
            }
        },
        [action, elements, organizationId, prepareCheckoutSession, stripe],
    );

    return {
        paymentBusy,
        stripeError,
        checkoutError,
        handleEnroll,
    };
}
